import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { creativeService } from "../api/services/creativeService";
import {
  CreativeFilters,
  CreativesResponse,
  UpdateCreativePayload,
  CreativeFormData,
} from "../schemas/creativeSchema";

export const useCreatives = (
  filters: CreativeFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const getFileCount = (fileValue: unknown): number => {
    if (!fileValue) return 0;
    if (Array.isArray(fileValue)) return fileValue.length;
    if (typeof FileList !== "undefined" && fileValue instanceof FileList) {
      return fileValue.length;
    }
    return 1;
  };

  const {
    data: creatives,
    isLoading: isCreativesLoading,
    refetch,
  } = useQuery<CreativesResponse, Error>({
    queryKey: ["creatives", filters],
    queryFn: () => creativeService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createCreativeMutation = useMutation({
    mutationFn: creativeService.create,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["creatives"] });
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      const createdCount = getFileCount((variables as CreativeFormData)?.file);
      if (createdCount > 1) {
        toast.success(`${createdCount} creatives created successfully`);
        return;
      }
      toast.success("Creative created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create creative");
    },
  });

  const updateCreativeMutation = useMutation({
    mutationFn: creativeService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creatives"] });
      queryClient.invalidateQueries({ queryKey: ["screen-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["playlist-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Creative updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update creative");
    },
  });

  const deleteCreativeMutation = useMutation({
    mutationFn: creativeService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["creatives"] });
      // A delete detaches the creative from every playlist holding it, so the
      // playlist lists, their durations and any open assignment view are all
      // stale now — not just the creative list.
      queryClient.invalidateQueries({ queryKey: ["playlists"] });
      queryClient.invalidateQueries({ queryKey: ["playlist-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["screen-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Creative deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete creative");
    },
  });

  return {
    creatives,
    refetchCreatives: refetch,
    createCreative: createCreativeMutation.mutate,
    updateCreative: updateCreativeMutation.mutate,
    deleteCreatives: deleteCreativeMutation.mutate,
    isDeletingCreatives: deleteCreativeMutation.isPending,
    isLoading:
      isCreativesLoading ||
      createCreativeMutation.isPending ||
      updateCreativeMutation.isPending ||
      deleteCreativeMutation.isPending,
  };
};
